import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { addToQueue, updateQueueItem, deleteQueueItem, getQueue } from '../lib/db';

const SyncContext = createContext(null);
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random());

export function SyncProvider({ children }) {
  const [queue, setQueue] = useState([]);
  const [simOffline, setSimOffline] = useState(false);
  const [browserOnline, setBrowserOnline] = useState(navigator.onLine);
  const syncingRef = useRef(false);

  const online = browserOnline && !simOffline;

  const refresh = useCallback(async () => {
    setQueue(await getQueue());
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const on = () => setBrowserOnline(true);
    const off = () => setBrowserOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  const notify = () => window.dispatchEvent(new Event('sync-updated'));

  const processQueue = useCallback(async () => {
    if (syncingRef.current || !(browserOnline && !simOffline)) return;
    syncingRef.current = true;
    try {
      let items = await getQueue();
      for (const item of items) {
        if (item.status === 'synced') {
          // proof already on server; drop the local copy only after the UI had time to refetch it
          if (Date.now() - (item.syncedAt || 0) > 30000) await deleteQueueItem(item.id);
          continue;
        }
        await updateQueueItem(item.id, { status: 'syncing', error: '' });
        await refresh();
        try {
          const uploadAll = async (list) => {
            const ids = [];
            for (const f of list || []) {
              const form = new FormData();
              form.append('file', f.blob, f.name);
              const { data } = await api.post('/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
              ids.push(data.id);
            }
            return ids;
          };
          const fileIds = await uploadAll(item.files);
          const removalFileIds = await uploadAll(item.removalFiles);
          const { data: actionRes } = await api.post(`/sites/${item.siteId}/actions`, {
            action_type: item.payload.action_type,
            campaign_id: item.payload.campaign_id || null,
            removal_campaign_id: item.payload.removal_campaign_id || null,
            client_user_id: item.payload.client_user_id || null,
            client_name: item.payload.client_name || null,
            gps: item.payload.gps || null,
            timestamp: item.payload.timestamp,
            notes: item.payload.notes || '',
            file_ids: fileIds,
            removal_file_ids: removalFileIds,
            inspection_id: item.payload.inspection_id || null,
            client_action_id: item.id,
          });
          if (actionRes?.auto_swapped) toast.info('Record auto-corrected to the other wall');
          await updateQueueItem(item.id, { status: 'synced', syncedAt: Date.now() });
          await refresh();
          notify();
        } catch (e) {
          await updateQueueItem(item.id, { status: 'failed', error: e.response?.data?.detail || e.message });
          await refresh();
        }
      }
    } finally {
      syncingRef.current = false;
    }
  }, [browserOnline, simOffline, refresh]);

  // auto sync when online returns
  useEffect(() => {
    if (online) processQueue();
  }, [online, processQueue]);

  // periodic retry
  useEffect(() => {
    const t = setInterval(() => { if (online) processQueue(); }, 15000);
    return () => clearInterval(t);
  }, [online, processQueue]);

  const enqueueAction = useCallback(async ({ siteId, action_type, campaign_id, removal_campaign_id, client_user_id, client_name, notes, gps, files, removalFiles, inspection_id }) => {
    const norm = (list) => (list || []).map((f) => {
      const b = f.blob || f;
      return { blob: b, name: f.name || b.name || 'capture.jpg', type: b.type };
    });
    const item = {
      id: uid(),
      siteId,
      payload: { action_type, campaign_id, removal_campaign_id, client_user_id, client_name, notes, gps, inspection_id, timestamp: new Date().toISOString() },
      files: norm(files),
      removalFiles: norm(removalFiles),
      status: online ? 'queued' : 'saved_locally',
      createdAt: new Date().toISOString(),
      error: '',
    };
    await addToQueue(item);
    await refresh();
    notify();
    if (online) processQueue();
    return item;
  }, [online, processQueue, refresh]);

  const retryItem = useCallback(async (id) => {
    await updateQueueItem(id, { status: 'queued', error: '' });
    await refresh();
    processQueue();
  }, [processQueue, refresh]);

  const pendingCount = queue.filter((q) => q.status !== 'synced').length;
  const failedCount = queue.filter((q) => q.status === 'failed').length;

  return (
    <SyncContext.Provider value={{
      queue, online, simOffline, setSimOffline, pendingCount, failedCount,
      enqueueAction, processQueue, retryItem, refresh,
    }}>
      {children}
    </SyncContext.Provider>
  );
}

export const useSync = () => useContext(SyncContext);
